import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuthState";
import { Link, useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { 
  User, 
  Edit3, 
  Save, 
  X, 
  Mail, 
  Phone, 
  MapPin,
  Calendar,
  Settings,
  Trophy,
  Target,
  Gift,
  Share2,
  Hash,
  TrendingUp,
  Award,
  ExternalLink,
  CreditCard,
  AlertCircle
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { UpdateUserProfile } from "@shared/schema";

interface ProfileStats {
  totalEntries: number;
  activeCompetitions: number;
  totalReferrals: number;
  completedChallenges: number;
  winnings: number;
}

// South African ID number validation
function validateSouthAfricanID(idNumber: string): { isValid: boolean; error?: string } {
  // Remove any spaces or special characters
  const cleaned = idNumber.replace(/\D/g, '');
  
  // Must be exactly 13 digits
  if (cleaned.length !== 13) {
    return { isValid: false, error: 'South African ID number must be exactly 13 digits' };
  }

  // Extract components
  const year = parseInt(cleaned.substring(0, 2), 10);
  const month = parseInt(cleaned.substring(2, 4), 10);
  const day = parseInt(cleaned.substring(4, 6), 10);
  
  // Validate month (01-12)
  if (month < 1 || month > 12) {
    return { isValid: false, error: 'Invalid month in ID number' };
  }
  
  // Validate day (01-31)
  if (day < 1 || day > 31) {
    return { isValid: false, error: 'Invalid day in ID number' };
  }
  
  // Validate check digit using Luhn algorithm - Process from right to left
  const digits = cleaned.split('').map(Number);
  let sum = 0;
  
  // Process the first 12 digits from right to left (excluding the check digit)
  for (let i = 11; i >= 0; i--) {
    const position = 11 - i; // Position from the right (0-based)
    if (position % 2 === 0) {
      // Even positions from right: double the digit
      const doubled = digits[i] * 2;
      sum += doubled > 9 ? doubled - 9 : doubled;
    } else {
      // Odd positions from right: add as is
      sum += digits[i];
    }
  }
  
  const checkDigit = (10 - (sum % 10)) % 10;
  
  if (checkDigit !== digits[12]) {
    return { isValid: false, error: 'Invalid ID number - check digit verification failed' };
  }
  
  return { isValid: true };
}

export default function MobileProfile() {
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [idValidationError, setIdValidationError] = useState<string | null>(null);
  const [profileFormData, setProfileFormData] = useState<UpdateUserProfile>({
    firstName: "",
    lastName: "",
    phone: "",
    dateOfBirth: "",
    gender: "",
    location: "",
    interests: "",
    idNumber: ""
  });

  // Fetch profile data from optimized dashboard endpoint
  const { data: dashboard, isLoading: dashboardLoading } = useQuery<any>({
    queryKey: ["/api/user/dashboard-optimized"],
    staleTime: 30 * 1000,
  });

  // Fetch user's referral code and stats
  const { data: referralData } = useQuery<{ referralCode: string; referralUrl: string; referredCount: number }>({
    queryKey: ["/api/referral-code"],
    enabled: !!user,
  });

  // Extract profile from dashboard response
  const profile = dashboard?.profile as {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    dateOfBirth?: string;
    gender?: string;
    location?: string;
    interests?: string;
    idNumber?: string;
  } | undefined;

  // Extract stats from dashboard
  const stats: ProfileStats = {
    totalEntries: dashboard?.stats?.totalEntries || 0,
    activeCompetitions: dashboard?.stats?.activeCompetitions || 0,
    totalReferrals: dashboard?.stats?.totalReferrals || 0,
    completedChallenges: dashboard?.stats?.activeChallenges || 0, // Changed to activeChallenges
    winnings: dashboard?.stats?.winnings || 0
  };

  // Profile update mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (profileData: Partial<UpdateUserProfile>) => {
      return await apiRequest("PUT", "/api/user/profile", { body: profileData });
    },
    onSuccess: () => {
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/user/dashboard-optimized"] });
      setIsEditingProfile(false);
      setProfileFormData({
        firstName: "",
        lastName: "",
        phone: "",
        dateOfBirth: "",
        gender: "",
        location: "",
        interests: "",
        idNumber: ""
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Separate mutation for instant marketing consent updates
  const updateMarketingConsentMutation = useMutation({
    mutationFn: async (marketingConsent: boolean) => {
      return await apiRequest("PUT", "/api/user/profile", { body: { marketingConsent } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/dashboard-optimized"] });
      toast({
        title: "Preference Updated",
        description: "Your marketing communication preference has been saved.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error updating preference",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Profile helper functions
  const handleProfileEditToggle = () => {
    if (isEditingProfile) {
      setProfileFormData({
        firstName: "",
        lastName: "",
        phone: "",
        dateOfBirth: "",
        gender: "",
        location: "",
        interests: "",
        idNumber: ""
      });
    } else {
      setProfileFormData({
        firstName: profile?.firstName || "",
        lastName: profile?.lastName || "",
        phone: profile?.phone || "",
        dateOfBirth: profile?.dateOfBirth || "",
        gender: profile?.gender || "",
        location: profile?.location || "",
        interests: profile?.interests || "",
        idNumber: profile?.idNumber || "",
      });
    }
    setIsEditingProfile(!isEditingProfile);
  };

  const handleProfileInputChange = (field: string, value: string) => {
    setProfileFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Validate ID number if it's being changed
    if (field === 'idNumber') {
      if (value.trim() === '') {
        setIdValidationError(null);
      } else {
        const validation = validateSouthAfricanID(value);
        setIdValidationError(validation.isValid ? null : validation.error || null);
      }
    }
  };

  const handleProfileSubmit = () => {
    // Validate ID number before submitting
    if (profileFormData.idNumber && profileFormData.idNumber.trim() !== '') {
      const validation = validateSouthAfricanID(profileFormData.idNumber);
      if (!validation.isValid) {
        setIdValidationError(validation.error || 'Invalid ID number');
        toast({
          title: "Invalid ID Number",
          description: validation.error || 'Please enter a valid South African ID number',
          variant: "destructive",
        });
        return;
      }
    }

    updateProfileMutation.mutate(profileFormData);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: "Signed Out",
        description: "You have been successfully signed out.",
      });
    } catch (error) {
      toast({
        title: "Sign Out Failed", 
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (dashboardLoading) {
    return <MobileProfileSkeleton />;
  }

  // Profile requires authentication - redirect to auth if user is not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <User className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Sign In Required
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Please sign in to access your profile.
          </p>
          <Link href="/auth">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              Sign In to Profile
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
      {/* Mobile Header */}
      <div className="text-white" style={{ background: 'linear-gradient(to right, #0e1b90, #1e2bb0)' }}>
        <div className="px-4 pt-12 pb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3 flex-1">
              <div className="bg-white/20 rounded-full p-3">
                <User className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Profile</h1>
                <p className="text-blue-100 text-sm">
                  {user?.firstName && user?.lastName 
                    ? `${user.firstName} ${user.lastName}` 
                    : user?.email || 'User'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleProfileEditToggle}
              className="text-white hover:bg-white/20"
              data-testid="button-edit-profile"
            >
              {isEditingProfile ? <X className="h-4 w-4" /> : <Edit3 className="h-4 w-4" />}
            </Button>
          </div>
          
          {/* User Badge */}
          <div className="bg-white/20 rounded-lg p-3">
            <div className="text-center">
              <Badge className="bg-white text-blue-600 mb-2">
                {user?.role === 'admin' ? 'Administrator' : 'Member'}
              </Badge>
              <div className="text-sm text-blue-100">
                Member since {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Stats Overview - Hidden on mobile */}
        <Card className="hidden">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center">
              <Trophy className="h-5 w-5 mr-2 text-yellow-600" />
              Your Stats
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{stats.activeCompetitions}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Active Competitions</div>
              </div>
              <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{stats.completedChallenges}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Active Challenges</div>
              </div>
              <div className="text-center p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{stats.totalReferrals}</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Referrals</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Information */}
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-lg flex items-center">
              <User className="h-5 w-5 mr-2 text-blue-600" />
              Profile Information
            </CardTitle>
            {!isEditingProfile && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleProfileEditToggle}
                data-testid="button-edit-profile-info"
              >
                <Edit3 className="h-4 w-4 mr-1" />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {isEditingProfile ? (
              <>
                {/* Edit Form */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="firstName" className="text-sm">First Name</Label>
                    <Input
                      id="firstName"
                      value={profileFormData.firstName}
                      onChange={(e) => handleProfileInputChange('firstName', e.target.value)}
                      placeholder="Enter first name"
                      className="mt-1"
                      data-testid="input-firstName"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName" className="text-sm">Last Name</Label>
                    <Input
                      id="lastName"
                      value={profileFormData.lastName}
                      onChange={(e) => handleProfileInputChange('lastName', e.target.value)}
                      placeholder="Enter last name"
                      className="mt-1"
                      data-testid="input-lastName"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="phone" className="text-sm">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={profileFormData.phone}
                    onChange={(e) => handleProfileInputChange('phone', e.target.value)}
                    placeholder="27821234567"
                    className="mt-1"
                    data-testid="input-phone"
                  />
                </div>

                <div>
                  <Label htmlFor="location" className="text-sm">Location</Label>
                  <Input
                    id="location"
                    value={profileFormData.location}
                    onChange={(e) => handleProfileInputChange('location', e.target.value)}
                    placeholder="Enter your location"
                    className="mt-1"
                    data-testid="input-location"
                  />
                </div>

                <div>
                  <Label htmlFor="dateOfBirth" className="text-sm">Date of Birth</Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={profileFormData.dateOfBirth}
                    onChange={(e) => handleProfileInputChange('dateOfBirth', e.target.value)}
                    className="mt-1"
                    data-testid="input-dateOfBirth"
                  />
                </div>

                <div>
                  <Label htmlFor="gender" className="text-sm">Gender</Label>
                  <Select 
                    value={profileFormData.gender} 
                    onValueChange={(value) => handleProfileInputChange('gender', value)}
                  >
                    <SelectTrigger className="mt-1" data-testid="select-gender">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="interests" className="text-sm">Interests</Label>
                  <Textarea
                    id="interests"
                    value={profileFormData.interests}
                    onChange={(e) => handleProfileInputChange('interests', e.target.value)}
                    placeholder="Tell us about your interests..."
                    className="mt-1 min-h-[80px]"
                    data-testid="input-interests"
                  />
                </div>

                {/* Temporarily hidden - ID Number field
                <div>
                  <Label htmlFor="idNumber" className="text-sm">ID Number</Label>
                  <div className="space-y-2">
                    <Input
                      id="idNumber"
                      value={profileFormData.idNumber}
                      onChange={(e) => handleProfileInputChange('idNumber', e.target.value)}
                      placeholder="Enter your South African ID number"
                      className={`mt-1 ${idValidationError ? 'border-red-500 focus:border-red-500' : ''}`}
                      data-testid="input-id-number"
                    />
                    {idValidationError && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{idValidationError}</AlertDescription>
                      </Alert>
                    )}
                  </div>
                </div>
                */}

                {/* Action Buttons */}
                <div className="flex space-x-2 pt-2">
                  <Button 
                    onClick={handleProfileSubmit}
                    disabled={updateProfileMutation.isPending}
                    className="flex-1"
                    data-testid="button-save-profile"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleProfileEditToggle}
                    className="flex-1"
                    data-testid="button-cancel-edit"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Display Mode */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium">Email</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{user?.email}</div>
                    </div>
                  </div>
                  
                  {profile?.phone && (
                    <div className="flex items-center space-x-3">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium">Phone</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{profile.phone}</div>
                      </div>
                    </div>
                  )}

                  {profile?.location && (
                    <div className="flex items-center space-x-3">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium">Location</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{profile.location}</div>
                      </div>
                    </div>
                  )}

                  {profile?.dateOfBirth && (
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium">Date of Birth</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {new Date(profile.dateOfBirth).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Temporarily hidden - ID Number display
                  {profile?.idNumber && (
                    <div className="flex items-center space-x-3">
                      <CreditCard className="h-4 w-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium">ID Number</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          ***{profile.idNumber.slice(-4)}
                        </div>
                      </div>
                    </div>
                  )}
                  */}

                  {(!profile?.firstName && !profile?.lastName && !profile?.phone && !profile?.location && !profile?.idNumber) && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <User className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Complete your profile to get started</p>
                    </div>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Referral Code - Hidden on mobile */}
        {/* {referralData?.referralCode && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center">
                <Share2 className="h-5 w-5 mr-2 text-purple-600" />
                Your Referral Code
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Share this link:</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(referralData.referralUrl || '');
                      toast({
                        title: "Copied!",
                        description: "Referral link copied to clipboard.",
                      });
                    }}
                    data-testid="button-copy-referral"
                  >
                    <Hash className="h-4 w-4 mr-1" />
                    Copy Link
                  </Button>
                </div>
                <div className="text-2xl font-bold text-purple-600">
                  {referralData.referralCode}
                </div>
                {referralData.referredCount > 0 && (
                  <p className="text-sm text-green-600 dark:text-green-400 mt-2 font-medium">
                    🎉 {referralData.referredCount} friend{referralData.referredCount !== 1 ? 's' : ''} referred!
                  </p>
                )}
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                  Friends get special bonuses when they use your referral link!
                </p>
              </div>
            </CardContent>
          </Card>
        )} */}

        {/* Marketing Preferences */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center">
              <Settings className="h-5 w-5 mr-2 text-blue-600" />
              Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div className="flex-1">
                <Label htmlFor="marketingConsent" className="text-base font-medium">
                  Marketing Communications
                </Label>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Receive updates about new competitions and promotions
                </p>
              </div>
              <Switch
                id="marketingConsent"
                checked={profile?.marketingConsent || false}
                onCheckedChange={(checked) => {
                  updateMarketingConsentMutation.mutate(checked);
                }}
                disabled={updateMarketingConsentMutation.isPending}
                data-testid="switch-marketing-consent"
              />
            </div>
          </CardContent>
        </Card>

        {/* Settings */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center">
              <Settings className="h-5 w-5 mr-2 text-gray-600" />
              Account
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button 
              variant="outline" 
              className="w-full justify-start h-12"
              onClick={handleSignOut}
              data-testid="button-sign-out"
            >
              <ExternalLink className="h-4 w-4 mr-3" />
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>

    </div>
  );
}

function MobileProfileSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 overflow-x-hidden">
      {/* Header Skeleton */}
      <div className="text-white px-4 pt-12 pb-6" style={{ background: 'linear-gradient(to right, #0e1b90, #1e2bb0)' }}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Skeleton className="h-12 w-12 rounded-full bg-white/20" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-32 bg-white/20" />
                <Skeleton className="h-4 w-24 bg-white/20" />
              </div>
            </div>
            <Skeleton className="h-8 w-8 bg-white/20" />
          </div>
          <Skeleton className="h-16 rounded-lg bg-white/20" />
        </div>
      </div>

      {/* Content Skeleton */}
      <div className="px-4 py-6 space-y-6">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

    </div>
  );
}